import { SingleValueBarFormat } from '@/common/components/builder/organisms/Charts';
import {
  CHART_KEY_DAILY_DATE_FORMAT,
  formatDate,
  getFirstDayOfWeek,
  getLastDayOfWeek,
  getSelectedDateRangeString,
  isDateBetween,
} from '@common/utils';
import { add, differenceInDays, getMonth, getWeekOfMonth, getYear, isAfter, isBefore, isSameYear } from 'date-fns';
import { SequencingSamplesSerie, SampleViewGraphDynamicOptions } from '../types';

const DYNAMIC_MAX_DAILY_DAYS = 15;
const DYNAMIC_MAX_WEEKLY_DAYS = 90;

export const DATE_FORMAT_DAILY = 'EEE d';
export const DATE_FORMAT_WEEKLY = 'MMM d';
export const DATE_FORMAT_MONTHLY = 'MMM yyyy';

export const defaultDynamicOptions: SampleViewGraphDynamicOptions = {
  dailyDateFormat: DATE_FORMAT_DAILY,
  weeklyDateFormat: DATE_FORMAT_WEEKLY,
  monthlyDateFormat: DATE_FORMAT_MONTHLY,
};

const buildBarData = (groupValue: string, value: number = 0): SingleValueBarFormat => {
  return {
    groupValue,
    value,
  };
};

/**
 * Returns a string to represent a daily data given a sequence group value and a date format.
 * @param sequencingSampleSerie object that represents a sequence sample.
 * @param dateFormat
 * @returns date format string
 */
const generateDailyGroupName = ({ groupValue }: SequencingSamplesSerie, dateFormat: string): string => {
  return formatDate(groupValue, dateFormat);
};

/**
 * Returns a string to represent a weekly data given a sequence group value and a date format.
 * @param sequencingSampleSerie object that represents a sequence sample.
 * @returns string representing weekly view graph.
 */
const generateWeeklyGroupName = (
  { groupValue }: SequencingSamplesSerie,
  _dateFormat: string,
  startDate?: Date,
  endDate?: Date,
): string => {
  const groupValueWithoutTZ = groupValue.replace('Z', '');
  const date: Date = new Date(groupValueWithoutTZ);

  if (startDate !== undefined && endDate !== undefined && isDateBetween(date, startDate, endDate)) {
    let firstDayOfWeek: Date = getFirstDayOfWeek(date);
    let lastDayOfWeek: Date = getLastDayOfWeek(date);
    if (isBefore(firstDayOfWeek, startDate)) firstDayOfWeek = startDate;
    if (isAfter(lastDayOfWeek, endDate)) lastDayOfWeek = endDate;
    return getSelectedDateRangeString(firstDayOfWeek, lastDayOfWeek, true);
  }
  return '';
};

/**
 * Returns a string to represent a monthly data given a sequence group value and a date format.
 * @param sequencingSampleSerie object that represents a sequence sample.
 * @param dateFormat
 * @returns date format string
 */
const generateMonthlyGroupName = ({ groupValue }: SequencingSamplesSerie, dateFormat: string): string => {
  return formatDate(groupValue, dateFormat);
};

/**
 * Generates graph data given sequence samples array.
 * @param data Sequence samples array.
 * @param graphData Initial data to repesent the graph.
 * @param dateFormat date format string
 * @param buildGroupName Function to build the name of the grpah data
 * @returns Graph data record object
 */
const generateGraphData = (
  data: SequencingSamplesSerie[],
  graphData: Record<string, SingleValueBarFormat>,
  dateFormat: string,
  buildGroupName: (data: SequencingSamplesSerie, dateFormat: string, startDate?: Date, endDate?: Date) => string,
  startDate?: Date,
  endDate?: Date,
): Record<string, SingleValueBarFormat> => {
  data.forEach((item) => {
    const groupName = buildGroupName(item, dateFormat, startDate, endDate);
    if (graphData[groupName] !== undefined && graphData[groupName]) {
      graphData[groupName].value += item.value;
    }
  });
  return graphData;
};

/**
 * Generates daily graph data given sequence samples array.
 * @param data Sequence samples array.
 * @param startDate date object to start building graph data from
 * @param endDate date object to end building graph data
 * @param dateFormat date format string
 * @returns Graph data record object
 */
export const buildDailyData = (
  data: SequencingSamplesSerie[],
  startDate: Date,
  endDate: Date,
  dateFormat: string,
): Record<string, SingleValueBarFormat> => {
  let graphData: Record<string, SingleValueBarFormat> = {};
  for (let d = new Date(startDate); d <= new Date(endDate); d.setDate(d.getDate() + 1)) {
    const groupName = formatDate(d, dateFormat);
    const key = formatDate(d, CHART_KEY_DAILY_DATE_FORMAT);
    graphData[key] = buildBarData(groupName);
  }

  graphData = generateGraphData(data, graphData, CHART_KEY_DAILY_DATE_FORMAT, generateDailyGroupName);
  return graphData;
};

/**
 * Generates weekly graph data given sequence samples array.
 * @param data Sequence samples array.
 * @param startDate date object to start building graph data from
 * @param endDate date object to end building graph data
 * @param dateFormat date format string
 * @returns Graph data record object
 */
export const buildWeeklyData = (
  data: SequencingSamplesSerie[],
  startDate: Date,
  endDate: Date,
  dateFormat: string,
): Record<string, SingleValueBarFormat> => {
  const firstWeek: number = getWeekOfMonth(startDate);
  const secondWeek: number = firstWeek + 1;
  const lastWeek: number = getWeekOfMonth(endDate);
  let graphData: Record<string, SingleValueBarFormat> = {};
  let firstDayOfWeek: Date = startDate;
  let lastDayOfWeek: Date = getLastDayOfWeek(startDate);

  for (let w = firstWeek; w <= lastWeek; w++) {
    if (w === secondWeek) {
      firstDayOfWeek = add(getFirstDayOfWeek(startDate), {
        weeks: 1,
      });
      lastDayOfWeek = add(lastDayOfWeek, {
        weeks: 1,
      });
    } else if (w !== firstWeek && w !== lastWeek) {
      firstDayOfWeek = add(firstDayOfWeek, {
        weeks: 1,
      });
      lastDayOfWeek = add(lastDayOfWeek, {
        weeks: 1,
      });
    } else if (w === lastWeek) {
      firstDayOfWeek = add(firstDayOfWeek, {
        weeks: 1,
      });
      lastDayOfWeek = endDate;
    }

    const groupName = getSelectedDateRangeString(firstDayOfWeek, lastDayOfWeek, true);

    graphData[groupName.toString()] = buildBarData(groupName);
  }
  graphData = generateGraphData(data, graphData, dateFormat, generateWeeklyGroupName, startDate, endDate);

  return graphData;
};

/**
 * Generates monthly graph data given sequence samples array.
 * @param data Sequence samples array.
 * @param startDate date object to start building graph data from
 * @param endDate date object to end building graph data
 * @param dateFormat date format string
 * @returns Graph data record object
 */
export const buildMonthlyData = (
  data: SequencingSamplesSerie[],
  startDate: Date,
  endDate: Date,
  dateFormat: string,
): Record<string, SingleValueBarFormat> => {
  let graphData: Record<string, SingleValueBarFormat> = {};

  const startDateMonth = getMonth(startDate);
  const endDateMonth = getMonth(endDate);

  if (isSameYear(startDate, endDate)) {
    for (let i = startDateMonth; i <= endDateMonth; i++) {
      const groupName = formatDate(new Date(new Date().getFullYear(), i, 1), dateFormat);
      graphData[groupName.toString()] = buildBarData(groupName);
    }
  } else {
    const startDateYear = getYear(startDate);
    const endDateYear = getYear(endDate);

    for (let y = startDateYear; y <= endDateYear; y++) {
      if (y === startDateYear) {
        for (let m = startDateMonth; m <= 11; m++) {
          const groupName = formatDate(new Date(y, m, 1), dateFormat);
          graphData[groupName.toString()] = buildBarData(groupName);
        }
      } else if (y === endDateYear) {
        for (let m = 0; m <= endDateMonth; m++) {
          const groupName = formatDate(new Date(y, m, 1), dateFormat);
          graphData[groupName.toString()] = buildBarData(groupName);
        }
      } else {
        for (let i = 0; i <= 11; i++) {
          const groupName = formatDate(new Date(y, i, 1), dateFormat);
          graphData[groupName.toString()] = buildBarData(groupName);
        }
      }
    }
  }

  graphData = generateGraphData(data, graphData, dateFormat, generateMonthlyGroupName);

  return graphData;
};

/**
 * Generates dynamic graph data based on given date range and sequence samples array.
 * @param data Sequence samples array.
 * @param startDate date object to start building graph data from
 * @param endDate date object to end building graph data
 * @returns Graph data object with data and according date format
 */
export const buildDynamicDateRange = (
  data: SequencingSamplesSerie[],
  startDate: Date,
  endDate: Date,
  options: Partial<SampleViewGraphDynamicOptions> = defaultDynamicOptions,
): {
  dateFormat: string;
  data: Record<string, SingleValueBarFormat>;
} => {
  let graphData: Record<string, SingleValueBarFormat> = {};
  let dateFormat;

  const diffInDays = differenceInDays(endDate, startDate);

  if (diffInDays <= DYNAMIC_MAX_DAILY_DAYS) {
    dateFormat = options.dailyDateFormat ?? defaultDynamicOptions.dailyDateFormat;
    graphData = { ...buildDailyData(data, startDate, endDate, dateFormat) };
  } else if (diffInDays > DYNAMIC_MAX_DAILY_DAYS && diffInDays <= DYNAMIC_MAX_WEEKLY_DAYS) {
    dateFormat = options.weeklyDateFormat ?? defaultDynamicOptions.weeklyDateFormat;
    graphData = { ...buildWeeklyData(data, startDate, endDate, dateFormat) };
  } else {
    dateFormat = options.monthlyDateFormat ?? defaultDynamicOptions.monthlyDateFormat;
    graphData = { ...buildMonthlyData(data, startDate, endDate, dateFormat) };
  }

  return {
    dateFormat,
    data: graphData,
  };
};
